import { subscribeAppResume } from "../services/appResumeService";
import { useEffect, useState, useRef } from "react";
import { getPremiumStatus, savePremiumConfig as savePremiumConfigRequest } from "../services/premiumService";

// ── App Store uyumluluğu ──────────────────────────────────────────────────
// Gerçek bir StoreKit / In-App Purchase entegrasyonu bağlanana kadar bu false
// kalmalı. PremiumPopup, bu false olduğu sürece fiyat + "satın al" CTA'sı
// göstermez (sadece "Çok Yakında" durumu) — aksi halde Apple App Review
// "çalışmayan buton" (Guideline 2.1) ve "IAP olmadan dijital ürün satışı"
// (Guideline 3.1.1) gerekçeleriyle reddeder. Gerçek IAP eklenince bunu true
// yapın ve purchasePremium() fonksiyonunu gerçek StoreKit çağrısına bağlayın.
export const IAP_ENABLED = false;

export const DEFAULT_PREMIUM_CONFIG = {
  premiumEnabled: false,
  limits: {
    alisverisOge:      10,
    aktifGorev:        10,
    aylikTakvim:       10,
    aylikFinans:       15,
    saglikKayitKisi:   10,
    cocukProfil:       1,
    belgeSayisi:       5,
    belgeDepoMB:       30,
    mesajGecmis:       30,
    kayitliYer:        3,
    aileUye:           3,
    kullaniciAile:     1,
    premiumPlusToplamUye: 5,
    gunlukNePisirsam:  2,
  },
  lockedFeatures: {
    finansRapor:         true,
    finansBirikim:       true,
    finansTaksit:        true,
    finansAbonelik:      true,
    finansFatura:        true,
    canliKonum:          true,
    geofence:            true,
    konumGecmisi:        true,

    evcilHayvanProfil:   true,
    haftalikMenu:        true,
    cokluSohbetOdasi:    true,
    zamanlanmisBildirim: true,
    otomatikBildirim:    true,
    cokluAile:           true,
  },
};

export function usePremium({ user, familyId }) {
  const [userIsPremium, setUserIsPremium] = useState(false);
  const [familyIsPremium, setFamilyIsPremium] = useState(false);
  const [familyIsPremiumPlus, setFamilyIsPremiumPlus] = useState(false);
  const [premiumConfig, setPremiumConfig] = useState(DEFAULT_PREMIUM_CONFIG);
  const [loading, setLoading] = useState(true);

  const stateRef = useRef({
    userIsPremium: false,
    familyIsPremium: false,
    familyIsPremiumPlus: false,
    premiumConfig: DEFAULT_PREMIUM_CONFIG,
  });

  const updateRef = (key, val) => { stateRef.current[key] = val; };

  const normalizeConfig = (config = {}) => ({
    premiumEnabled: typeof config.premiumEnabled === "boolean" ? config.premiumEnabled : DEFAULT_PREMIUM_CONFIG.premiumEnabled,
    limits: {
      ...DEFAULT_PREMIUM_CONFIG.limits,
      ...(config.limits || {}),
    },
    lockedFeatures: {
      ...DEFAULT_PREMIUM_CONFIG.lockedFeatures,
      ...(config.lockedFeatures || {}),
    },
  });

  const applyStatus = (status = {}) => {
    const cfg = normalizeConfig(status.premiumConfig || status.config || DEFAULT_PREMIUM_CONFIG);
    const userPremium = !!status.userIsPremium;
    const familyPlus = !!status.familyIsPremiumPlus;
    const familyPremium = !!status.familyIsPremium || familyPlus;

    setPremiumConfig(cfg);
    setUserIsPremium(userPremium);
    setFamilyIsPremium(familyPremium);
    setFamilyIsPremiumPlus(familyPlus);

    updateRef("premiumConfig", cfg);
    updateRef("userIsPremium", userPremium);
    updateRef("familyIsPremium", familyPremium);
    updateRef("familyIsPremiumPlus", familyPlus);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async ({ silent = false } = {}) => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      try {
        const status = await getPremiumStatus(familyId);
        if (!cancelled) applyStatus(status);
      } catch (err) {
        console.error("usePremium backend status error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    const unsubscribeResume = subscribeAppResume(() => load({ silent: true }), { delayMs: 600 });
    const onPremiumRefresh = () => load({ silent: true });
    const onDataChanged = (event) => {
      const detail = event?.detail || {};
      if (detail.resource !== "premium") return;
      if (detail.familyId && familyId && String(detail.familyId) !== String(familyId)) return;
      load({ silent: true });
    };
    window.addEventListener("fp:premiumRefresh", onPremiumRefresh);
    window.addEventListener("fp:dataChanged", onDataChanged);

    return () => {
      cancelled = true;
      unsubscribeResume();
      window.removeEventListener("fp:premiumRefresh", onPremiumRefresh);
      window.removeEventListener("fp:dataChanged", onDataChanged);
    };
  }, [user?.uid, familyId]);

  const calcIsPremium = (cfg, uPrem, fPrem) => {
    if (!cfg.premiumEnabled) return true;
    return uPrem || fPrem;
  };

  const isPremium = calcIsPremium(premiumConfig, userIsPremium, familyIsPremium);

  const checkLimit = (key, currentCount) => {
    const { premiumConfig: cfg, userIsPremium: uP, familyIsPremium: fP } = stateRef.current;
    if (calcIsPremium(cfg, uP, fP)) return { allowed: true, limit: null, remaining: Infinity };
    const limit = cfg.limits[key];
    if (limit == null) return { allowed: true, limit: null, remaining: Infinity };
    return { allowed: currentCount < limit, limit, remaining: limit - currentCount };
  };

  const checkFeature = (key) => {
    const { premiumConfig: cfg, userIsPremium: uP, familyIsPremium: fP } = stateRef.current;
    if (calcIsPremium(cfg, uP, fP)) return { allowed: true };
    return { allowed: !cfg.lockedFeatures[key] };
  };

  const purchasePremium = async (_scope = "user") => {
    return false;
  };

  const restorePurchase = async () => {
    try {
      const status = await getPremiumStatus(familyId);
      applyStatus(status);
      return !!status.userIsPremium || !!status.familyIsPremium || !!status.familyIsPremiumPlus;
    } catch {
      return false;
    }
  };

  return {
    isPremium,
    userIsPremium,
    familyIsPremium,
    familyIsPremiumPlus,
    loading,
    premiumConfig,
    checkLimit,
    checkFeature,
    purchasePremium,
    restorePurchase,
    iapEnabled: IAP_ENABLED,
  };
}

export async function savePremiumConfig(config) {
  const saved = await savePremiumConfigRequest(config);
  try { window.dispatchEvent(new Event("fp:premiumRefresh")); } catch {}
  return saved;
}
